<template>
  <div
    :class="{
      container: screenSize.desktop,
      'ls-inbox': !screenSize.desktop,
      'current-property': currentProperty,
    }"
  >
    <div
      v-if="currentProperty && ((!screenSize.desktop && !showConversation) || screenSize.desktop)"
      class="ls-inbox--address-wrapper"
      test-id="address-card"
    >
      <LsAddressCard :property="currentProperty" />
    </div>

    <BLoading :active.sync="anythingLoading" />
    <div v-if="!anythingLoading" class="columns is-mobile">
      <Transition name="inbox">
        <div
          v-if="showInboxOnMobile"
          :class="[
            'column ls-inbox--left',
            { 'is-4': screenSize.desktop, 'is-12': !screenSize.desktop },
          ]"
        >
          <h1 class="ls-inbox--left-header">Conversations</h1>

          <div v-if="Boolean(conversation.conversations.length)" class="ls-inbox--outer-wrapper">
            <div
              v-for="section in conversations"
              :key="section.contractor.contractor_id"
              class="ls-inbox--crew-select"
            >
              <div
                :class="{
                  'ls-inbox--selected': section.contractor.user.user_id === contractorUserId,
                }"
              >
                <MessageCrewSelectItem
                  :item="section"
                  test-id="crew_select"
                  :current-property-id="PROPERTY_MANAGER__CURRENT_PROPERTY_ID"
                  @click="loadConversation"
                />
              </div>
            </div>
          </div>
          <!-- below is unique for contacts -->
          <div
            v-for="section in CONVERSATION__UNDUPED_CONTACTS"
            :key="section.contractor_id"
            class="ls-inbox--crew-select"
          >
            <div
              :class="{
                'ls-inbox--selected': section.user_id === contractorUserId,
              }"
            >
              <MessageCrewSelectContactItem :item="section" @click="loadConversation" />
            </div>
          </div>
          <p
            v-if="!screenSize.desktop && !Boolean(conversation.conversations.length)"
            class="ls-inbox-view--placholder-text"
          >
            There are no current conversations.
          </p>
        </div>
      </Transition>
      <Transition name="conversation">
        <div
          v-if="showConversation"
          id="ls-inbox-view--conversation"
          :class="[
            'ls-inbox-view--conversation',
            {
              'column is-8': screenSize.desktop,
              'column is-12': !screenSize.desktop,
            },
          ]"
        >
          <ConversationMessages :current-property="currentProperty" />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import {
  CONVERSATION__GET_CONVERSATIONS,
  CONVERSATION__GET_CONVERSATION,
  CONVERSATION__START_CONVERSATION,
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  CONVERSATION__UNDUPED_CONTACTS,
  PROPERTY_MANAGER__CURRENT_PROPERTY_ID,
} from '@/store/types';
import MessageCrewSelectItem from './MessageCrewSelectItem/MessageCrewSelectItem.vue';
import ConversationMessages from './Conversation/ConversationMessages.vue';
import _ from 'lodash';
import { lsRouterHelpers } from 'ls-vue-common';
import MessageCrewSelectContactItem from '@/views/Authorized/InboxView/MessageCrewSelectItem/MessageCrewSelectContactItem.vue';
import { LsAddressCard } from '@/components/common';

export default {
  name: 'InboxView',
  components: {
    MessageCrewSelectItem,
    MessageCrewSelectContactItem,
    ConversationMessages,
    LsAddressCard,
  },
  data() {
    return {
      isLoading: true,
      contractorUserId: null,
    };
  },
  computed: {
    ...mapState(['conversation', 'screenSize', 'properties']),
    ...mapGetters([CONVERSATION__UNDUPED_CONTACTS, PROPERTY_MANAGER__CURRENT_PROPERTY_ID]),
    anythingLoading() {
      return (
        this.isLoading ||
        this.properties.loadingStatus.isLoading ||
        this.conversation.loadingStatus.isLoading
      );
    },
    showInboxOnMobile() {
      return !lsRouterHelpers.getParam(this.$route, 'conversation_id') || this.screenSize.desktop;
    },
    showConversation() {
      return (
        (lsRouterHelpers.getParam(this.$route, 'conversation_id') && !this.screenSize.desktop) ||
        this.screenSize.desktop
      );
    },
    currentProperty() {
      if (this[PROPERTY_MANAGER__CURRENT_PROPERTY_ID]) {
        return this.properties.propertiesById[this[PROPERTY_MANAGER__CURRENT_PROPERTY_ID]];
      }

      return null;
    },
    conversations() {
      const { conversations } = this.conversation;

      if (this.currentProperty) {
        const sortedConversations = _.orderBy(conversations, (conversation) => {
          const thisProperty = _.find(conversation.customer.properties, (property) => {
            return property.id === this.currentProperty.id;
          });

          return thisProperty;
        });

        return sortedConversations || conversations;
      }

      return conversations;
    },
  },
  watch: {
    screenSize: {
      handler() {
        if (this.screenSize.desktop) {
          this.loadInitialConversation();
        }
        if (!this.screenSize.desktop) {
          // hide ConversationMessages instantly when resizing to mobile to avoid animation overlap
          const conversation = document.getElementById('ls-inbox-view--conversation');
          const style = _.get(conversation, 'style', {});
          _.set(style, 'display', 'none');

          this.contractorUserId = null;

          this.$router.replace({
            path: '/inbox',
            name: 'inbox',
          });
        }
      },
      deep: true,
    },
    $route: {
      handler() {
        const conversation_id = lsRouterHelpers.getParam(this.$route, 'conversation_id');
        if (!conversation_id) {
          this.contractorUserId = null;
        }
        if (conversation_id) {
          this.conversation_id = conversation_id;
        }
      },
    },
  },
  async created() {
    await Promise.all([
      this[PROPERTIES__GET_PROPERTIES_AND_SCHEDULES](),
      this[CONVERSATION__GET_CONVERSATIONS](),
    ]);

    this.loadInitialConversation();
  },
  methods: {
    ...mapActions([
      CONVERSATION__GET_CONVERSATIONS,
      CONVERSATION__GET_CONVERSATION,
      CONVERSATION__START_CONVERSATION,
      PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
    ]),
    async loadInitialConversation() {
      if (lsRouterHelpers.getParam(this.$route, 'conversation_id')) {
        await this[CONVERSATION__GET_CONVERSATION]({
          conversation_id: lsRouterHelpers.getParam(this.$route, 'conversation_id'),
        });

        this.contractorUserId = _.get(
          this.conversation.currentConversation.contractor,
          'user.user_id'
        );

        if (_.isNil(this.contractorUserId)) {
          return this.$router.push({ name: 'inbox' });
        }

        this.isLoading = false;
        return;
      }

      if (this.conversation.conversations.length && this.screenSize.desktop) {
        let userId = _.get(this.conversations, '[0].contractor.user.user_id');

        // super gross but this gets us by for now
        if (this.currentProperty) {
          const conversationForThisProperty = _.find(this.conversations, (conversation) => {
            return _.find(_.get(conversation, 'customer.properties'), (property) => {
              return property.id === this.currentProperty.id;
            });
          });

          const thisPropertyInConversation = _.find(
            _.get(conversationForThisProperty, 'customer.properties'),
            (property) => {
              return property.id === this.currentProperty.id;
            }
          );

          if (thisPropertyInConversation) {
            const schedule = _.find(this.currentProperty.schedules, (schedule) => {
              return (
                _.get(schedule, 'contractor.user.user_id') ===
                _.get(thisPropertyInConversation, 'schedules[0].contractor.user.user_id')
              );
            });

            const user = _.get(schedule, 'contractor.user');

            userId = user ? user.user_id : null;
          } else {
            userId = null;
          }
        }

        this.contractorUserId = userId;

        if (this.contractorUserId) {
          await this[CONVERSATION__START_CONVERSATION]({
            contractorUserId: this.contractorUserId,
          });

          this.goToConversation();
        }
      }

      this.isLoading = false;
    },
    async loadConversation({ conversation_id, contractorUserId }) {
      this.contractorUserId = contractorUserId;
      if (conversation_id) {
        this.goToConversation(conversation_id);
        await this[CONVERSATION__GET_CONVERSATION]({
          conversation_id: conversation_id,
        });

        return;
      }

      // needs to be sequential
      await this[CONVERSATION__START_CONVERSATION]({
        contractorUserId: contractorUserId,
      });
      await this[CONVERSATION__GET_CONVERSATIONS]();

      this.goToConversation();
    },
    goToConversation(value) {
      this.$router.replace({
        name: 'inboxConversation',
        params: {
          conversation_id: value || this.conversation.currentConversation.conversation_id,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ls-inbox {
  padding: 0 0px 0 18px;
}

.ls-inbox--outer-wrapper {
  width: 100%;
}

.inbox-enter-active,
.inbox-leave-active,
.conversation-enter-active,
.conversation-leave-active {
  transition: all 0.5s ease-in-out;
}

.inbox-enter,
.inbox-leave-to {
  opacity: 0;
  transform: translateX(-600px);
}

.conversation-enter,
.conversation-leave-to {
  opacity: 0;
  transform: translateX(600px);
}

.ls-inbox--left {
  height: 85vh;
  position: absolute;
  border-right: 2px solid $primary-light;
}

.ls-inbox--left-header {
  padding: 10px 10px 20px 0px;
}

.ls-inbox--crew-select {
  width: 104%;
  padding: 6px 0;
  border-top: 2px solid $primary-light;
}

.ls-inbox--selected {
  width: 100%;
  background-color: $primary-light;
  position: relative;
  &:after {
    border-top: 15px solid transparent;
    border-bottom: 15px solid transparent;
    border-left: 15px solid $primary-light;
    content: '';
    height: 0;
    width: 0;
    position: absolute;
    right: -15px;
    top: calc(50% - 15px);
  }
}

.ls-inbox-view--conversation {
  height: 100%;
  position: absolute;
  left: 324px;
  top: 0px;
  padding-left: 20px;
}

.ls-inbox-view--placholder-text {
  height: 90%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: $size-4;
  color: $primary;
}

@media (max-width: $mobile-max-width) {
  .inbox-enter-active,
  .inbox-leave-active,
  .inbox-enter,
  .inbox-leave-to,
  .conversation-enter-active,
  .conversation-leave-active,
  .conversation-enter,
  .conversation-leave-to {
    // avoid awkward input box shift durring animation
    overflow-y: hidden;
  }

  .ls-inbox--address-wrapper {
    padding-right: 20px;
    margin-top: 10px;
  }

  .ls-inbox--left {
    border-right: none;
  }

  .ls-inbox--crew-select {
    width: 98%;
  }

  #ls-inbox-view--conversation {
    height: 100%;
    left: 0;
    padding-left: 10px;
  }

  .ls-inbox--selected {
    background-color: $primary-light;
    &:after {
      border: none;
      content: '';
    }
  }
}
.themed-app {
  .ls-inbox--left {
    border-color: $theme-color-primary-light;
  }
  .ls-inbox--crew-select {
    border-color: $theme-color-primary-light;
  }
  .ls-inbox--selected {
    background-color: $theme-color-primary-light;
    &:after {
      border-color: $theme-color-primary-light;
    }
  }
  .ls-inbox-view--placholder-text {
    font-size: $theme-txt-body-lg;
    color: $theme-color-primary;
  }

  @media (max-width: $mobile-max-width) {
    .ls-inbox--selected {
      background-color: $theme-color-primary-light;
    }
  }
}
</style>
